import Config from '@config';
import { CACHE_EXPIRATION_TIME } from '@gasbi/shared/common/features/constants';
import {
  FiltersGrid,
  Pagination,
  infiniteQueryOptions,
  normalizeInfiniteQueryData,
} from '@gasbi/shared/index';
import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  ClarifyPaymentRequest,
  DistributePaymentRequest,
  UndistributedPayment,
} from './types';

export const undistributedPaymentsApi = createApi({
  reducerPath: 'undistributedPaymentsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: Config.VITE_API_URL + '/undistributed-payments',
  }),
  tagTypes: ['UndistributedPayments'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getUndistributedPayments: builder.query<UndistributedPayment[], void>({
      query: params => ({
        url: ``,
        params,
      }),
      providesTags: ['UndistributedPayments'],
    }),
    getUndistributedPaymentsI: builder.infiniteQuery<
      UndistributedPayment[],
      FiltersGrid | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: '',
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['UndistributedPayments'],
    }),
    updateUndistributedPaymentsById: builder.mutation<
      UndistributedPayment,
      UndistributedPayment
    >({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data: data,
      }),
      invalidatesTags: result => [
        'UndistributedPayments',
        { type: 'UndistributedPayments', id: result?.id },
      ],
    }),
    distributePayment: builder.mutation<void, DistributePaymentRequest>({
      query: ({ id, accountId }) => ({
        url: `/${id}/distribute`,
        method: 'PUT',
        data: { accountId },
      }),
      invalidatesTags: ['UndistributedPayments'],
    }),
    clarifyPayment: builder.mutation<void, ClarifyPaymentRequest>({
      query: ({ id, ...data }) => ({
        url: `/${id}/clarify`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['UndistributedPayments'],
    }),
  }),
});

export const {
  useGetUndistributedPaymentsQuery,
  useDistributePaymentMutation,
  useClarifyPaymentMutation,
} = undistributedPaymentsApi;

export const useGetUndistributedPaymentsInfiniteQuery =
  normalizeInfiniteQueryData(
    undistributedPaymentsApi.useGetUndistributedPaymentsIInfiniteQuery,
  );
